(function () {
  if (customElements.get('collection-product-slider')) {
    return;
  }

  class CollectionProductSlider extends HTMLElement {
    constructor() {
      super();

      this.currentIndex = 0;
      this.raf = null;
      this.scrollEndTimer = null;
      this.resizeObserver = null;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-slider-viewport]');
      this.track = this.querySelector('[data-slider-track]');
      this.slides = Array.from(this.querySelectorAll('[data-slider-slide]'));
      this.prevButton = this.querySelector('[data-slider-prev]');
      this.nextButton = this.querySelector('[data-slider-next]');
      this.dotsContainer = this.querySelector('[data-slider-dots]');

      if (!this.viewport || !this.track || !this.slides.length) {
        return;
      }

      this.prevButton?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.currentIndex + 1));
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });

      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.viewport);

      this.refresh();
    }

    disconnectedCallback() {
      this.viewport?.removeEventListener('scroll', this.onScroll);
      this.resizeObserver?.disconnect();

      if (this.raf) {
        window.cancelAnimationFrame(this.raf);
      }

      window.clearTimeout(this.scrollEndTimer);
    }

    get perPage() {
      return this.isMobile ? 2 : 4;
    }

    get isMobile() {
      return window.matchMedia('(max-width: 767px)').matches;
    }

    get slidesPerMove() {
      return this.isMobile ? 2 : 1;
    }

    get maxIndex() {
      return Math.max(this.getSnapPoints().length - 1, 0);
    }

    getStep() {
      if (this.slides.length < 2) {
        return this.slides[0]?.getBoundingClientRect().width || 0;
      }

      return Math.abs(this.slides[1].offsetLeft - this.slides[0].offsetLeft);
    }

    getSnapPoints() {
      const maxFirstVisibleSlide = Math.max(this.slides.length - this.perPage, 0);
      const points = [];

      for (let index = 0; index < maxFirstVisibleSlide; index += this.slidesPerMove) {
        points.push(index);
      }

      points.push(maxFirstVisibleSlide);

      return [...new Set(points)];
    }

    getSlideIndexForPage(pageIndex) {
      return this.getSnapPoints()[pageIndex] || 0;
    }

    getClosestPageIndex(slideIndex) {
      const points = this.getSnapPoints();

      return points.reduce((closestIndex, point, index) => {
        const closestDistance = Math.abs(points[closestIndex] - slideIndex);
        const distance = Math.abs(point - slideIndex);

        return distance < closestDistance ? index : closestIndex;
      }, 0);
    }

    refresh() {
      this.currentIndex = Math.min(this.currentIndex, this.maxIndex);
      this.createDots();
      this.updateControls();
    }

    createDots() {
      if (!this.dotsContainer) {
        return;
      }

      this.dotsContainer.innerHTML = '';

      const points = this.getSnapPoints();

      points.forEach((slideIndex, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'collection-product-slider__dot';
        dot.setAttribute('aria-label', `Go to products ${slideIndex + 1} to ${Math.min(slideIndex + this.perPage, this.slides.length)}`);
        dot.addEventListener('click', () => this.goTo(index));
        this.dotsContainer.appendChild(dot);
      });
    }

    goTo(index) {
      const nextIndex = Math.max(0, Math.min(index, this.maxIndex));
      const step = this.getStep();
      const slideIndex = this.getSlideIndexForPage(nextIndex);

      this.currentIndex = nextIndex;
      this.viewport.scrollTo({
        left: step * slideIndex,
        behavior: 'smooth'
      });
      this.updateControls();
    }

    onScroll() {
      if (this.raf) {
        window.cancelAnimationFrame(this.raf);
      }

      this.raf = window.requestAnimationFrame(() => {
        const step = this.getStep();

        if (step > 0) {
          const slideIndex = Math.round(this.viewport.scrollLeft / step);
          this.currentIndex = this.getClosestPageIndex(slideIndex);
        }

        this.updateControls();
      });

      window.clearTimeout(this.scrollEndTimer);

      if (this.isMobile) {
        this.scrollEndTimer = window.setTimeout(() => {
          this.goTo(this.currentIndex);
        }, 140);
      }
    }

    onResize() {
      this.refresh();
      this.goTo(this.currentIndex);
    }

    updateControls() {
      const hasOverflow = this.maxIndex > 0;

      if (this.prevButton) {
        this.prevButton.disabled = !hasOverflow || this.currentIndex <= 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled = !hasOverflow || this.currentIndex >= this.maxIndex;
      }

      if (this.dotsContainer) {
        Array.from(this.dotsContainer.children).forEach((dot, index) => {
          const isActive = index === this.currentIndex;
          dot.classList.toggle('is-active', isActive);
          dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        });
      }
    }
  }

  customElements.define('collection-product-slider', CollectionProductSlider);
})();
